import { buildApiUrl, Type, sendGetRequest, sendPostRequest } from '@/api/api';
import { assertSchemaAndCoerce } from '@/shared/utils/schemas/assertions'
import { z } from 'zod'

export type TariffPeriod = z.infer<typeof tariffPeriodSchema>
export type Tariffs = z.infer<typeof tariffSchema>
export type FlattenedTariff = {
  TariffId: number;
  ProviderId: number;
  ProviderName: string;
  TariffName: string;
  Source: string;
  TransZone: string | null;
  Voltage: string | null;
  IsTimeOfUse: boolean;
  IsApproved: boolean;

  Block: string[];
  Rate: number[];
  OldRate: number[];
  RateCP: number[];

  PeakW: number[];
  OldPeakW: number[];
  PeakWCP: number[];
  StandardW: number[];
  OldStandardW: number[];
  StandardWCP: number[];
  OffPeakW: number[];
  OldOffPeakW: number[];
  OffPeakWCP: number[];

  PeakS: number[];
  OldPeakS: number[];
  PeakSCP: number[];
  StandardS: number[];
  OldStandardS: number[];
  StandardSCP: number[];
  OffPeakS: number[];
  OldOffPeakS: number[];
  OffPeakSCP: number[];

  BasicChargeW: number[];
  OldBasicChargeW: number[];
  BasicChargeWCP: number[];
  DemandChargeW: number[];
  OldDemandChargeW: number[];
  DemandChargeWCP: number[];
  AccessChargeW: number[];
  OldAccessChargeW: number[];
  AccessChargeWCP: number[];
  ServiceChargeW: number[];
  OldServiceChargeW: number[];
  ServiceChargeWCP: number[];
  CapacityChargeW: number[];
  OldCapacityChargeW: number[];
  CapacityChargeWCP: number[];
  ReactiveChargeW: number[];
  OldReactiveChargeW: number[];
  ReactiveChargeWCP: number[];
  LegacyChargeW: number[];
  OldLegacyChargeW: number[];
  LegacyChargeWCP: number[];
  NetworkDemandChargeW: number[];
  OldNetworkDemandChargeW: number[];
  NetworkDemandChargeWCP: number[];
  GenerationChargeW: number[];
  OldGenerationChargeW: number[];
  GenerationChargeWCP: number[];
  ElectrificationChargeW: number[];
  OldElectrificationChargeW: number[];
  ElectrificationChargeWCP: number[];
  AffordabilityChargeW: number[];
  OldAffordabilityChargeW: number[];
  AffordabilityChargeWCP: number[];
  AncillaryChargeW: number[];
  OldAncillaryChargeW: number[];
  AncillaryChargeWCP: number[];
  CreditReverseChargeW: number[];
  OldCreditReverseChargeW: number[];
  CreditReverseChargeWCP: number[];

  BasicChargeS: number[];
  OldBasicChargeS: number[];
  BasicChargeSCP: number[];
  DemandChargeS: number[];
  OldDemandChargeS: number[];
  DemandChargeSCP: number[];
  AccessChargeS: number[];
  OldAccessChargeS: number[];
  AccessChargeSCP: number[];
  ServiceChargeS: number[];
  OldServiceChargeS: number[];
  ServiceChargeSCP: number[];
  CapacityChargeS: number[];
  OldCapacityChargeS: number[];
  CapacityChargeSCP: number[];
  ReactiveChargeS: number[];
  OldReactiveChargeS: number[];
  ReactiveChargeSCP: number[];
  LegacyChargeS: number[];
  OldLegacyChargeS: number[];
  LegacyChargeSCP: number[];
  NetworkDemandChargeS: number[];
  OldNetworkDemandChargeS: number[];
  NetworkDemandChargeSCP: number[];
  GenerationChargeS: number[];
  OldGenerationChargeS: number[];
  GenerationChargeSCP: number[];
  ElectrificationChargeS: number[];
  OldElectrificationChargeS: number[];
  ElectrificationChargeSCP: number[];
  AffordabilityChargeS: number[];
  OldAffordabilityChargeS: number[];
  AffordabilityChargeSCP: number[];
  AncillaryChargeS: number[];
  OldAncillaryChargeS: number[];
  AncillaryChargeSCP: number[];
  CreditReverseChargeS: number[];
  OldCreditReverseChargeS: number[];
  CreditReverseChargeSCP: number[];
  BandId: number[];
  IsActive: boolean[];
  YearOfTariff: number;
};


export const tariffPeriodSchema = z.object({
  Id: z.number(),
  ChargeName: z.string(),
  Season: z.string(),
  TimeOfUse: z.string(),
  StartOfPeriod: z.number().nullable(),
  EndOfPeriod: z.number().nullable(),
  RateInCents: z.number(),
  OldRateInCents: z.number().nullable(),
  RateChangePercent: z.number().nullable(),
  IsActive: z.boolean(),
  YearOfTariff: z.number()
})

export const tariffSchema = z.array(z.object({
  Id: z.number(),
  UtilityProviderId: z.number(),
  UtilityProviderName: z.string(),
  Name: z.string(),
  TransZone: z.string().nullable(),
  Voltage: z.string().nullable(),
  IsTimeOfUse: z.boolean(),
  IsApproved: z.boolean(),
  ExternalId: z.string().nullable(),
  GroupKey: z.string().nullable(),
  Periods: z.array(tariffPeriodSchema)
}))

function buildParams(extra: Record<string, string>, page?: number, pageSize?: number): URLSearchParams {
  const params = new URLSearchParams(extra)
  if (page) params.set('page', page.toString())
  if (pageSize) params.set('pageSize', pageSize.toString())
  return params
}

export async function fetchTariffs(ids: number[], yearOfTariff: string, page?: number, pageSize?: number) {
  const isCompare = yearOfTariff.startsWith('Compare')

  if (isCompare) {
    const url = buildApiUrl(Type.TariffManagement, `tariffs/unapproved?${buildParams({}, page, pageSize)}`)
    const { data, meta } = await sendGetRequest(url)
    return { data: assertSchemaAndCoerce(data, tariffSchema), meta:
     assertSchemaAndCoerce(meta, z.object({ Page: z.number(), PageSize: z.number(), TotalCount: z.number() })) }
  }

  const params = buildParams({ yearOfTariff }, page, pageSize)
  const url = buildApiUrl(Type.TariffManagement, `tariffs?${params.toString()}`)
  const { data, meta } = await sendPostRequest(url, { tariffStructureIds: ids })
  return { data: assertSchemaAndCoerce(data, tariffSchema), meta: assertSchemaAndCoerce(meta, z.object({ Page: z.number(), PageSize: z.number(), TotalCount: z.number() })) }
}
